import { removeFalsyValues } from '@shared/helpers';
/*                                               */
import { log } from '@shared/log.browser.ts';
import { createOnExFromServerData } from '@tilelist/application/core/OnExFactory.ts';
import type {
    TileListInitialSession,
    TileListSessionContext,
} from '@tilelist/application/core/TileListInitialSession.ts';
import { loadTileListPage } from '@tilelist/application/core/TileListPageLoader.ts';
import type { CacheKeyRefresher } from '@tilelist/application/ports/CacheKeyRefresher.ts';
import type { ClientReloadContextProvider } from '@tilelist/application/ports/ClientReloadContextProvider.ts';
import type { TileListRenderedPublisher } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { SelectionPage, SelectionSource, SelectionUpdate } from '@tilelist/application/ports/SelectionEvents.ts';
import type { TileListPageFetcher } from '@tilelist/application/ports/TileListPageFetcher.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import type { PageLoadStrategy } from '@tilelist/domain/composition/strategies/PageLoadStrategy.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { tileListQueryParamSchema } from './TileListRouteValidation.ts';

export type TileListPageLoadResult = {
    /*                                                */
    page: TileListPageLayout | null;
    /*                                                                                  */
    source: SelectionSource;
};

export type TileListPageSession = {
    snapshot: TileListSessionContext;
    onex: OnEx;
    /**
 *
 *
 *
 */
    reload: (update: SelectionUpdate) => Promise<TileListPageLoadResult>;
};

export type TileListSessionDeps = {
    /*                                                                            */
    pageFetcher: TileListPageFetcher;
    /*                                                                         */
    variationLoadingService: VariationLoader;
    reloadContextProvider: ClientReloadContextProvider;
    eventPublisher: TileListRenderedPublisher;
    /*                                                                 */
    cacheKeyRefresher: CacheKeyRefresher;
    /*                                                                  */
    strategy: PageLoadStrategy;
};

export function selectionPageToQueryParams(rule: string, page: SelectionPage) {
    return {
        rule,
        ...(page?.sortOrder !== undefined ? { sortiertnach: page.sortOrder } : {}),
        ...(page?.offset !== undefined ? { o: page.offset } : {}),
        ...(page?.layout !== undefined ? { l: page.layout } : {}),
        ...(page?.comparisonId !== undefined ? { c: page.comparisonId } : {}),
    };
}

/**
 *
 *
 *
 *
 *
 *
 */
export function createPageSession(
    initialSession: TileListInitialSession,
    deps: TileListSessionDeps,
): TileListPageSession {
    const snapshot = initialSession.session;
    const onex = createOnExFromServerData(snapshot.onexServerData);

    async function reload(update: SelectionUpdate): Promise<TileListPageLoadResult> {
        const { selection, source } = update;

        /*                                                                                 */
        /*                                               */
        const queryParamParseResult = tileListQueryParamSchema.safeParse(
            removeFalsyValues(selectionPageToQueryParams(selection.rule, selection.page)),
        );

        if (!queryParamParseResult.success) {
            log.error('Invalid query parameters:', queryParamParseResult.error);
            return { page: null, source };
        }

        const queryParams = queryParamParseResult.data;

        const reloadContext = await deps.reloadContextProvider.create(snapshot);

        const nextBracketResult = await loadTileListPage({
            mode: 'reload',
            pageFetcher: deps.pageFetcher,
            variationLoadingService: deps.variationLoadingService,
            strategy: deps.strategy,
            ctx: {
                queryParams,
                requestContext: reloadContext,
                onex,
            },
        });

        if (nextBracketResult.state !== 'success') {
            log.error('Failed to load tile list');
            return { page: null, source };
        }

        const pageView: TileListPageLayout = nextBracketResult.payload;

        await deps.cacheKeyRefresher.refresh();

        deps.eventPublisher.publishTileListRendered({
            rule: selection.rule,
            page: {
                ...(selection.page.sortOrder !== undefined ? { sortiertnach: selection.page.sortOrder } : {}),
                ...(selection.page.offset !== undefined ? { o: selection.page.offset } : {}),
                ...(selection.page.layout !== undefined ? { l: selection.page.layout } : {}),
                ...(selection.page.comparisonId !== undefined ? { c: selection.page.comparisonId } : {}),
            },
            redirect: selection.url,
            historic: source === 'historic',
            shouldScroll: source === 'historic' || source === 'page',
        });

        return { page: pageView, source };
    }

    return {
        snapshot,
        onex,
        reload,
    };
}
